/**
 * Native Android Network Status using Capacitor Network Plugin
 * Optimized for Android APK builds - uses native network detection
 */

import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

const isNative = Capacitor.isNativePlatform();

// For Android native app, always use Capacitor Network
// Only fallback if not in native (development/testing)
function getNetworkAPI() {
  if (isNative) {
    return Network;
  }
  // Development fallback only
  return null;
}

/**
 * Get current network status
 * @returns {Promise<Object>} Network status object
 */
export async function getNetworkStatus() {
  try {
    const NetworkAPI = getNetworkAPI();
    if (NetworkAPI) {
      const status = await NetworkAPI.getStatus();
      return {
        connected: status.connected,
        connectionType: status.connectionType || 'unknown',
        isOnline: status.connected,
      };
    }
    // Development fallback (shouldn't happen in APK)
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      connected: navigator.onLine,
      connectionType: connection ? connection.effectiveType || 'unknown' : 'unknown',
      isOnline: navigator.onLine,
    };
  } catch (error) {
    console.error('Error getting network status:', error);
    // Ultimate fallback
    return {
      connected: navigator.onLine || false,
      connectionType: 'unknown',
      isOnline: navigator.onLine || false,
    };
  }
}

/**
 * Add listener for network status changes
 * @param {Function} callback - Callback function(status)
 * @returns {Function} Unsubscribe function
 */
export async function addNetworkStatusListener(callback) {
  const NetworkAPI = getNetworkAPI();
  if (NetworkAPI) {
    try {
      const listener = NetworkAPI.addListener('networkStatusChange', (status) => {
        callback({
          connected: status.connected,
          connectionType: status.connectionType || 'unknown',
          isOnline: status.connected,
        });
      });

      // Also listen to browser events as backup (for hybrid scenarios)
      const handleOnline = () => callback({ connected: true, connectionType: 'unknown', isOnline: true });
      const handleOffline = () => callback({ connected: false, connectionType: 'none', isOnline: false });
      
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        listener.remove();
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    } catch (error) {
      console.warn('Failed to set up Capacitor network listener:', error);
    }
  }
  
  // Development fallback (shouldn't happen in APK)
  {
    // Browser-only implementation
    const handleOnline = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      callback({
        connected: true,
        connectionType: connection ? connection.effectiveType || 'unknown' : 'unknown',
        isOnline: true,
      });
    };
    
    const handleOffline = () => {
      callback({
        connected: false,
        connectionType: 'none',
        isOnline: false,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also listen to connection changes if available
    let connectionListener = null;
    if (navigator.connection || navigator.mozConnection || navigator.webkitConnection) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      connectionListener = () => {
        callback({
          connected: navigator.onLine,
          connectionType: connection.effectiveType || 'unknown',
          isOnline: navigator.onLine,
        });
      };
      connection.addEventListener('change', connectionListener);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connectionListener) {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection) {
          connection.removeEventListener('change', connectionListener);
        }
      }
    };
  }
}

